/** @jsxImportSource @emotion/react */
import { useState, useEffect, useRef } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import { useTranslation } from 'next-i18next'
import useFlow from 'modules/flow/state/use-flow'
import useOrder from '../../../order/state/order/use-order'
import useConfig from 'modules/config/state/use-config'

import {
  trackFormSubmitEvent,
  trackFormErrorEvent,
} from '../../../analytics/helpers/track-form-events'

import styled from '@emotion/styled'
import { DEFAULT_THEME_COLORS } from 'css/theme'
import { LordIcon } from '@classy/campaign-page-blocks'
import { checkboxContainerCss, animatedLabelCss } from './utils/checkbox-animation-css'
import { convertRgbOrRgbaToHex } from '../../../../utils/colors'
import IconContainer from '../../../ui/atoms/Layout/IconContainer'
import { Tooltip } from '../../../ui/atoms/Tooltip'
import { UiIcon } from '../../../ui/atoms/Icon'

import AmountCurrencyFrequencySection from './components/amount-currency-frequency-section'
import AppFlowTwoColumnStepLayout from 'modules/flow/components/two-column-step-layout/two-column-step-layout'
import CheckboxField from 'modules/ui/molecules/FormComponents/CheckboxField'
import Column from 'modules/ui/atoms/Layout/Column'
import LoadingSpinnerComponent from 'modules/ui/atoms/Loading/LoadingSpinner'
import Row from 'modules/ui/atoms/Layout/Row'
import { formatCurrency } from 'utils/functions/currencyUtils'
import { getCurrencyDecimalPlaces } from '../../../../utils/currency'

const StyledDiv = styled.div`
  display: flex;
  justify-content: center;
`

const DCFNudgeStep = () => {
  const { t, i18n } = useTranslation('common')
  const flow = useFlow()
  const order = useOrder()
  const config = useConfig()
  const lastTrackedErrorRef = useRef<string | null>(null)
  const initialFocusRef = useRef<HTMLInputElement>(null)

  // State to track the label animation
  const [labelText, setLabelText] = useState('')

  // State to track and show loading indicator if it's calculating fees
  const isLoading = order.dcf.value.isLoading

  type DonorCoveredFeesFormData = {
    dcf: boolean
  }

  // Initialize react-hook-form
  const methods = useForm<DonorCoveredFeesFormData>({
    defaultValues: {
      dcf: order.dcf.value.isChecked,
    },
  })

  // Determine if DCF checkbox is checked or not.
  const dcfChecked = methods.watch('dcf')

  // Values to display depending on the DCF checkbox
  const feeAmount = order.dcf.value.fee

  const uncheckedLabelText = t(
    'DCFNudgeStep.uncheckedLabelText',
    'Add {{feeAmount}} to maximize my gift',
    {
      feeAmount: formatCurrency(
        feeAmount.toFixed(getCurrencyDecimalPlaces(order.currency.value)),
        order.currency.value,
        i18n.language,
      ),
    },
  )

  const checkedLabelText = t('DCFNudgeStep.checkedLabelText', {
    defaultValue: 'Thank you for covering the fees!',
  })

  const uncheckedAmountValue = order.amount.value
  const checkedAmountValue = (order.amount.value ?? 0) + feeAmount

  // Focus modal’s first element when the component mounts
  useEffect(() => {
    if (initialFocusRef.current) {
      initialFocusRef.current.focus()
    }
  }, [])

  // Effect to handle label text animation
  useEffect(() => {
    const dcfLabelElement = document.getElementById('dcf-label')

    function triggerLabelAnimation() {
      if (dcfLabelElement) {
        dcfLabelElement.classList.remove('fade-out')
        // Force animation restart
        void dcfLabelElement.offsetWidth
        dcfLabelElement.classList.add('fade-out')
      }

      // Change the label when animation is halfway done (label opacity is 0 at the time).
      const timeout = setTimeout(() => {
        setLabelText(dcfChecked ? checkedLabelText : uncheckedLabelText)
      }, 200)

      return () => clearTimeout(timeout)
    }

    triggerLabelAnimation()
  }, [dcfChecked, uncheckedLabelText, checkedLabelText])

  // Move to the next step
  const onNext = () => {
    order.dcf.setValue({ isChecked: dcfChecked })

    const nextStep = flow.nextStep

    if (nextStep) {
      flow.goToStep(nextStep.name)
    }
  }

  // Handle form submission
  const onSubmit = methods.handleSubmit((data) => {
    // Track the form submission event
    trackFormSubmitEvent(flow.currentStep.name, { fees_covered: data.dcf })

    // Move to the next step
    onNext()
  })

  // Handle form validation errors
  const handleFormError = (errors: unknown) => {
    const currentError = JSON.stringify(errors)

    // Track error if it's different from the last tracked error
    if (lastTrackedErrorRef.current !== currentError) {
      trackFormErrorEvent(flow.currentStep.name, currentError)
      lastTrackedErrorRef.current = currentError
    }
  }

  return (
    <>
      <AppFlowTwoColumnStepLayout.Header
        title={flow.currentStep.title}
        backButtonDataId="dcf-nudge-step-back-button"
      />
      <AppFlowTwoColumnStepLayout.Body>
        <Column>
          <Row style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <LordIcon
              size={100}
              name={config.dcfNudge.iconName}
              animation={config.dcfNudge.iconAnimation}
              variant={config.dcfNudge.iconStyle}
              // check if the element has colors, if not load theme color
              primaryColor={
                config.dcfNudge.iconPrimaryColor ||
                convertRgbOrRgbaToHex(config.theme['--theme-color__brand-primary'])
              }
              secondaryColor={
                config.dcfNudge.iconSecondaryColor ||
                convertRgbOrRgbaToHex(config.theme['--theme-color__brand-primary'])
              }
            />
          </Row>
          <Row
            style={{
              minHeight: '2.5rem',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            {isLoading ? (
              <LoadingSpinnerComponent
                size="sm"
                color={convertRgbOrRgbaToHex(config.theme['--theme-color__brand-primary'])}
              />
            ) : (
              <AmountCurrencyFrequencySection
                amount={
                  dcfChecked
                    ? Number(
                        checkedAmountValue.toFixed(getCurrencyDecimalPlaces(order.currency.value)),
                      )
                    : uncheckedAmountValue
                }
                currency={order.currency.value}
                frequency={order.frequency.value}
                locale={i18n.language}
              />
            )}
          </Row>

          <span
            style={{
              color: DEFAULT_THEME_COLORS['--theme-color--neutral-20'],
              display: 'inline',
              justifyContent: 'center',
              alignItems: 'center',
              textAlign: 'center',
              padding: '10px',
              marginBottom: '1em',
            }}
          >
            {config.dcfNudge.appeal}
          </span>

          <span
            css={checkboxContainerCss(dcfChecked)}
            style={{
              marginInline: '-2rem',
              padding: '12px',
            }}
          >
            <FormProvider {...methods}>
              <form onSubmit={onSubmit} onError={handleFormError}>
                <StyledDiv>
                  <CheckboxField
                    id="dcf"
                    centered={true}
                    dataId="dcf-nudge-checkbox"
                    ref={initialFocusRef}
                  >
                    <span css={animatedLabelCss} id="dcf-label">
                      {labelText}
                    </span>
                  </CheckboxField>
                  <IconContainer top="4px">
                    <Tooltip
                      placement="bottom-start"
                      text={t(
                        'DCFNudgeStep.tooltip',
                        'Transaction fees include both processing and donation software charges.',
                      )}
                      textAlign="left"
                    >
                      <UiIcon name="info" color="#78726E" />
                    </Tooltip>
                  </IconContainer>
                </StyledDiv>
              </form>
            </FormProvider>
          </span>
        </Column>
      </AppFlowTwoColumnStepLayout.Body>

      <AppFlowTwoColumnStepLayout.Footer>
        <AppFlowTwoColumnStepLayout.FooterButton
          onClick={onSubmit}
          dataId="dcf-nudge-continue-button"
        >
          {t('DCFNudgeStep.continueButton', 'Continue')}
        </AppFlowTwoColumnStepLayout.FooterButton>
      </AppFlowTwoColumnStepLayout.Footer>
    </>
  )
}

export default DCFNudgeStep
